import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { ChevronDownIcon } from "@heroicons/react/24/outline";

import usePartDetails from "../../../data/usePartDetails";
import { IPart } from "../../../models/Part";
import classNames from "../../../utils/classNames";
import LoadingPulse from "../../shared/LoadingPulse";
import EbayListingTitle, { computeEbayListingTitle } from "../EbayListingTitle";

type Props = { part: IPart };

export default function UnlistedEbayTitle({ part }: Props) {
  const [titleBuilderOpen, setTitleBuilderOpen] = useState(false);

  const { partDetails, isLoading: detailsLoading } = usePartDetails(
    part.id,
    titleBuilderOpen
  );

  const fullPart = partDetails ?? part;

  return (
    <div
      className="flex min-w-0 flex-col border-l-2 border-sky-400 bg-gradient-to-r from-sky-50/80 via-sky-50/40 to-sky-50/80 px-3 py-2"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span className="hidden shrink-0 text-[10px] font-semibold uppercase tracking-wide text-sky-700 sm:inline">
          eBay Title
        </span>
        <div className="min-w-0 flex-1">
          <EbayListingTitle part={part} onlyTitle onTable />
        </div>
        <button
          type="button"
          onClick={() => setTitleBuilderOpen((previous) => !previous)}
          aria-expanded={titleBuilderOpen}
          aria-label={
            titleBuilderOpen
              ? "Hide title builder (merge tiles and highlights)"
              : "Show title builder (merge tiles and highlights)"
          }
          className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-md border border-sky-200 bg-white text-sky-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800"
        >
          <ChevronDownIcon
            className={classNames(
              "h-4 w-4 transition-transform",
              titleBuilderOpen && "rotate-180"
            )}
            aria-hidden
          />
        </button>
      </div>

      {titleBuilderOpen && (
        <div className="mt-2 border-t border-sky-200/70 pt-2">
          {detailsLoading && !partDetails ? (
            <div className="py-3">
              <LoadingPulse />
            </div>
          ) : (
            <div className="rounded-lg border border-sky-200/70 bg-white/70 p-3 shadow-sm">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-sky-700">
                eBay listing template
              </p>
              <EbayListingTitle
                key={fullPart.id}
                part={fullPart}
                onTable
                editDefault
                isTemplate={false}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
